import { ChevronDown, ChevronUp, Download, PackageSearch, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TablePagination from '../components/TablePagination';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagination } from '../hooks/usePagination';
import { orderTrackingService } from '../services/api';
import {
  getDeliveryHistoryTooltip,
  getDeliveryStatusClass,
  isDelayed,
  parsePLDate
} from './utils/ProductionOrderMonitoring';

const TABS = [
  { key: 'pending', label: 'Oczekujące materiały' },
  { key: 'predictions', label: 'Predykcja dostaw' },
  { key: 'oldnew', label: 'Nr mat. (stary/nowy)' },
];

const DEPARTMENTS = ['PVC', 'ALU'];
const DELIVERY_ADDRESSES = ['Nowa Ruda', 'Szalejów Górny'];

const getLatestChangeTimestamp = (item) => {
  const events = item.change_events?.length > 0
    ? item.change_events
    : (item.delivery_date_history || []);

  const timestamps = events
    .map(event => parsePLDate(event.changed_on)?.getTime())
    .filter(timestamp => Number.isFinite(timestamp));

  return timestamps.length > 0 ? Math.max(...timestamps) : null;
};

const ProductionOrderMonitoring = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'pending';
  // Unknown/garbage values from the URL fall back to PVC instead of hitting the API.
  const requestedDepartment = (searchParams.get('department') || '').toUpperCase();
  const department = DEPARTMENTS.includes(requestedDepartment) ? requestedDepartment : 'PVC';
  const deliveryAddressParam = searchParams.get('deliveryAddress') || '';
  const deliveryAddress = department === 'PVC' && DELIVERY_ADDRESSES.includes(deliveryAddressParam)
    ? deliveryAddressParam
    : '';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [duplicateAlertsByScope, setDuplicateAlertsByScope] = useState({
    pvcNowaRuda: [],
    pvcSzalejow: [],
    alu: [],
  });

  // Material lookup state
  const [lookupMaterial, setLookupMaterial] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  // Order delivery status lookup
  const [orderIdentifier, setOrderIdentifier] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [orderStatusError, setOrderStatusError] = useState(null);
  const [orderStatusLoading, setOrderStatusLoading] = useState(false);
  const [orderSuggestions, setOrderSuggestions] = useState([]);
  const [orderSuggestionsLoading, setOrderSuggestionsLoading] = useState(false);
  const [orderSuggestionsOpen, setOrderSuggestionsOpen] = useState(false);
  const [activeOrderSuggestion, setActiveOrderSuggestion] = useState(-1);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [sortByLatestChanges, setSortByLatestChanges] = useState(false);

  // Search / filter
  const [searchTerm, setSearchTerm] = useState('');
  // Filtering runs on the debounced value; clearing the box applies immediately.
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const activeSearchTerm = searchTerm.trim() === '' ? '' : debouncedSearchTerm;
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const fetchIdRef = useRef(0);
  const duplicateAlertFetchIdRef = useRef(0);
  const orderStatusFetchIdRef = useRef(0);
  const orderSuggestionFetchIdRef = useRef(0);
  const skipNextOrderSuggestionFetchRef = useRef(false);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setData([]);
    try {
      let result;
      if (activeTab === 'pending') {
        result = await orderTrackingService.getPendingMaterials(department, deliveryAddress);
      } else if (activeTab === 'predictions') {
        result = await orderTrackingService.getPredictions(department, deliveryAddress);
      } else if (activeTab === 'oldnew') {
        result = await orderTrackingService.getMaterialsOldNew(department, deliveryAddress);
      }
      if (fetchId !== fetchIdRef.current) return;
      setData(result || []);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab, department, deliveryAddress]);

  const fetchDuplicateOrderAlerts = useCallback(async () => {
    const fetchId = ++duplicateAlertFetchIdRef.current;
    const requests = await Promise.allSettled([
      orderTrackingService.getPossibleDuplicateOrders('PVC', 'Nowa Ruda'),
      orderTrackingService.getPossibleDuplicateOrders('PVC', 'Szalejów Górny'),
      orderTrackingService.getPossibleDuplicateOrders('ALU'),
    ]);
    if (fetchId !== duplicateAlertFetchIdRef.current) return;

    const alertsFor = (index, scope) => (
      requests[index].status === 'fulfilled'
        ? (requests[index].value || []).map(alert => ({ ...alert, duplicate_scope: scope }))
        : []
    );
    setDuplicateAlertsByScope({
      pvcNowaRuda: alertsFor(0, 'Nowa Ruda'),
      pvcSzalejow: alertsFor(1, 'Szalejów Górny'),
      alu: alertsFor(2, 'ALU'),
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchDuplicateOrderAlerts();
  }, [fetchDuplicateOrderAlerts]);

  // Fetch last sync info on mount
  useEffect(() => {
    const fetchLastSync = async () => {
      try {
        const result = await orderTrackingService.getLastSync();
        setLastSync(result);
      } catch (err) {
        // Silently fail - no last sync yet
        console.log('No last sync info available');
      }
    };
    fetchLastSync();
  }, []);

  // Reset lookup when tab or dept changes
  useEffect(() => {
    setLookupResult(null);
    setLookupError(null);
    setLookupMaterial('');
    setSortField(null);
    setSortDir('asc');
    setSortByLatestChanges(false);
    setSearchTerm('');
  }, [activeTab, department, deliveryAddress]);

  useEffect(() => {
    setOrderIdentifier('');
    setOrderStatus(null);
    setOrderStatusError(null);
    setOrderSuggestions([]);
    setOrderSuggestionsOpen(false);
    setActiveOrderSuggestion(-1);
    setOrderStatusLoading(false);
    orderStatusFetchIdRef.current += 1;
    orderSuggestionFetchIdRef.current += 1;
  }, [department, deliveryAddress]);

  useEffect(() => {
    const query = orderIdentifier.trim();
    const fetchId = ++orderSuggestionFetchIdRef.current;
    if (skipNextOrderSuggestionFetchRef.current) {
      skipNextOrderSuggestionFetchRef.current = false;
      return undefined;
    }
    if (query.length < 2) {
      setOrderSuggestions([]);
      setOrderSuggestionsLoading(false);
      setOrderSuggestionsOpen(false);
      setActiveOrderSuggestion(-1);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      setOrderSuggestionsLoading(true);
      try {
        const result = await orderTrackingService.getOrderIdentifierSuggestions(
          department,
          query,
          deliveryAddress
        );
        if (fetchId !== orderSuggestionFetchIdRef.current) return;
        setOrderSuggestions(result || []);
        setOrderSuggestionsOpen((result || []).length > 0);
        setActiveOrderSuggestion(-1);
      } catch (err) {
        if (fetchId !== orderSuggestionFetchIdRef.current) return;
        setOrderSuggestions([]);
        setOrderSuggestionsOpen(false);
      } finally {
        if (fetchId === orderSuggestionFetchIdRef.current) {
          setOrderSuggestionsLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [department, deliveryAddress, orderIdentifier]);
  // Close export menu when clicking outside
  useEffect(() => {
    if (!exportMenuOpen) return;
    
    const handleClickOutside = (event) => {
      if (!event.target.closest('.om-export-wrapper')) {
        setExportMenuOpen(false);
      }
    };
    
    // Delay attachment to prevent interfering with the opening click
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [exportMenuOpen]);
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await orderTrackingService.syncEmails();
      setSyncResult(result);
      // Refresh current data after sync
      fetchData();
      fetchDuplicateOrderAlerts();
      // Fetch updated last sync info
      const lastSyncResult = await orderTrackingService.getLastSync();
      setLastSync(lastSyncResult);
    } catch (err) {
      setError(err.response?.data?.detail || 'Email sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleMaterialLookup = async (e) => {
    e.preventDefault();
    if (!lookupMaterial.trim()) return;
    setLookupError(null);
    setLookupResult(null);
    try {
      const result = await orderTrackingService.getMaterialPrediction(
        department,
        lookupMaterial.trim(),
        deliveryAddress
      );
      setLookupResult(result);
    } catch (err) {
      setLookupError(err.response?.data?.detail || 'Material not found');
    }
  };

  const handleOrderStatusLookup = async (e) => {
    e.preventDefault();
    const identifier = orderIdentifier.trim();
    if (!identifier) return;
    const fetchId = ++orderStatusFetchIdRef.current;

    setOrderStatusLoading(true);
    setOrderSuggestionsOpen(false);
    setOrderStatus(null);
    setOrderStatusError(null);
    try {
      const result = await orderTrackingService.getOrderDeliveryStatus(
        department,
        identifier,
        deliveryAddress
      );
      if (fetchId !== orderStatusFetchIdRef.current) return;
      setOrderStatus(result);
    } catch (err) {
      if (fetchId !== orderStatusFetchIdRef.current) return;
      setOrderStatusError(err.response?.data?.detail || 'Nie udało się sprawdzić zamówienia');
    } finally {
      if (fetchId === orderStatusFetchIdRef.current) {
        setOrderStatusLoading(false);
      }
    }
  };

  const selectOrderSuggestion = (suggestion) => {
    skipNextOrderSuggestionFetchRef.current = true;
    setOrderIdentifier(suggestion.value);
    setOrderSuggestions([]);
    setOrderSuggestionsOpen(false);
    setActiveOrderSuggestion(-1);
    setOrderStatus(null);
    setOrderStatusError(null);
    setOrderStatusLoading(false);
    orderStatusFetchIdRef.current += 1;
  };

  const handleOrderIdentifierKeyDown = (event) => {
    if (!orderSuggestionsOpen || orderSuggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveOrderSuggestion(current =>
        current < orderSuggestions.length - 1 ? current + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveOrderSuggestion(current =>
        current > 0 ? current - 1 : orderSuggestions.length - 1
      );
    } else if (event.key === 'Escape') {
      setOrderSuggestionsOpen(false);
      setActiveOrderSuggestion(-1);
    } else if (event.key === 'Enter' && activeOrderSuggestion >= 0) {
      event.preventDefault();
      selectOrderSuggestion(orderSuggestions[activeOrderSuggestion]);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    setError(null);
    setExportMenuOpen(false);
    try {
      if (activeTab === 'pending') {
        await orderTrackingService.exportPendingMaterials(department, format, deliveryAddress);
      } else if (activeTab === 'predictions') {
        await orderTrackingService.exportPredictions(department, format, deliveryAddress);
      } else if (activeTab === 'oldnew') {
        await orderTrackingService.exportMaterialsOldNew(department, format, deliveryAddress);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ─── Sorting ───
  const handleSort = (field) => {
    setSortByLatestChanges(false);
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSortByLatestChanges = () => {
    setSortByLatestChanges(prev => !prev);
    if (!sortByLatestChanges) {
      setSortField(null);
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  // ─── Filtering + Sorting pipeline ───
  const processedData = useMemo(() => {
    let items = [...data];

    // filter
    if (activeSearchTerm.trim()) {
      const term = activeSearchTerm.toLowerCase();
      items = items.filter(item =>
        Object.values(item).some(v =>
          String(v).toLowerCase().includes(term)
        )
      );
    }

    // Sort by the date of the latest actual change, newest first.
    if (sortByLatestChanges) {
      items.sort((a, b) => {
        const aLatestChange = getLatestChangeTimestamp(a);
        const bLatestChange = getLatestChangeTimestamp(b);

        if (aLatestChange === null && bLatestChange === null) return 0;
        if (aLatestChange === null) return 1;
        if (bLatestChange === null) return -1;
        return bLatestChange - aLatestChange;
      });
    } else if (sortField) {
      // Regular field sort
      items.sort((a, b) => {
        let va = a[sortField];
        let vb = b[sortField];

        if (!va && !vb) return 0;
        if (!va) return 1;
        if (!vb) return -1;

        const da = parsePLDate(va);
        const db = parsePLDate(vb);
        if (da && db) {
          return sortDir === 'asc' ? da - db : db - da;
        }

        if (!isNaN(va) && !isNaN(vb)) {
          return sortDir === 'asc' ? va - vb : vb - va;
        }

        va = String(va ?? '').toLowerCase();
        vb = String(vb ?? '').toLowerCase();

        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }


    return items;
  }, [data, activeSearchTerm, sortByLatestChanges, sortField, sortDir]);

  const pagination = usePagination(processedData, {
    storageKey: 'production-order-monitoring-page-size',
  });

  const pvcDuplicateAlerts = [
    ...duplicateAlertsByScope.pvcNowaRuda,
    ...duplicateAlertsByScope.pvcSzalejow,
  ];
  const duplicateOrderAlerts = department === 'ALU'
    ? duplicateAlertsByScope.alu
    : deliveryAddress === 'Nowa Ruda'
      ? duplicateAlertsByScope.pvcNowaRuda
      : deliveryAddress === 'Szalejów Górny'
        ? duplicateAlertsByScope.pvcSzalejow
        : pvcDuplicateAlerts;

  const departmentHasDuplicateWarning = (dept) => (
    dept === 'ALU'
      ? duplicateAlertsByScope.alu.length > 0
      : pvcDuplicateAlerts.length > 0
  );

  const deliveryAddressHasDuplicateWarning = (address) => {
    if (!address) return pvcDuplicateAlerts.length > 0;
    if (address === 'Nowa Ruda') {
      return duplicateAlertsByScope.pvcNowaRuda.length > 0;
    }
    return duplicateAlertsByScope.pvcSzalejow.length > 0;
  };

  // ─── Helper to render availability with original date ───
  const renderAvailability = (item) => {
    const current = item.availability;
    const original = item.original_availability;
    const currentDisplay = current?.toLowerCase().includes('dost') && item.planned_delivery_date
      ? `${current} (${item.planned_delivery_date})`
      : current;
    
    // No dates at all
    if (!current && !original) return '—';
    
    // Only current date, or no original (no Winkhaus change)
    if (!original) {
      return currentDisplay || '—';
    }
    
    // Both dates exist and are different - show new date with original below
    return (
      <div style={{ lineHeight: '1.3' }}>
        <div><strong>{currentDisplay}</strong></div>
        <div style={{ 
          fontSize: '0.85em', 
          color: '#888', 
          textDecoration: 'line-through' 
        }}>
          {original}
        </div>
      </div>
    );
  };

  // ─── Table renderers ───
  const renderPendingTable = () => (
    <table className="om-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('order_number')} style={{ cursor: 'pointer' }}>Nr zam.{sortIndicator('order_number')}</th>
          <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}>Poz.{sortIndicator('position')}</th>
          <th onClick={() => handleSort('material_number')} style={{ cursor: 'pointer' }}>Nr mat.{sortIndicator('material_number')}</th>
          <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>Nazwa mat.{sortIndicator('description')}</th>
          <th onClick={() => handleSort('customer_order_designation')} style={{ cursor: 'pointer' }}>Zam. klienta{sortIndicator('customer_order_designation')}</th>
          <th onClick={() => handleSort('document_date')} style={{ cursor: 'pointer' }}>Data zam.{sortIndicator('document_date')}</th>
          <th onClick={() => handleSort('availability')} style={{ cursor: 'pointer' }}>Szac. dost.{sortIndicator('availability')}</th>
          <th onClick={() => handleSort('change_events_count')} style={{ cursor: 'pointer' }}>Zmiany{sortIndicator('change_events_count')}</th>
          <th onClick={() => handleSort('ordered')} style={{ cursor: 'pointer' }}>Zam.{sortIndicator('ordered')}</th>
          <th onClick={() => handleSort('delivered')} style={{ cursor: 'pointer' }}>Dost.{sortIndicator('delivered')}</th>
          <th onClick={() => handleSort('remaining')} style={{ cursor: 'pointer' }}>Pozost.{sortIndicator('remaining')}</th>
        </tr>
      </thead>
      <tbody>
        {pagination.pageItems.map((item) => {
          return (
          <tr key={`${item.order_number}:${item.position}:${item.material_number}`} className={isDelayed(item) ? 'om-row-delayed' : ''}>
            <td>{item.order_number}</td>
            <td>{item.position}</td>
            <td><strong>{item.material_number}</strong></td>
            <td>{item.description}</td>
            <td>{item.customer_order_designation || '—'}</td>
            <td>{item.document_date}</td>
            <td className={getDeliveryStatusClass(item)}
                style={{ cursor: item.delivery_date_history?.length > 0 || item.original_availability ? 'help' : 'default' }}
                title={getDeliveryHistoryTooltip(item)}>
              {renderAvailability(item)}
            </td>
            <td
              style={{ cursor: item.change_events_count > 0 ? 'help' : 'default' }}
              title={getDeliveryHistoryTooltip(item)}
            >
              {item.change_events_count || 0}{item.has_history_gap ? ' ⚠' : ''}
            </td>
            <td>{item.ordered}</td>
            <td>{item.delivered}</td>
            <td className="om-remaining">{item.remaining}</td>
          </tr>
        )})}
      </tbody>
    </table>
  );

  const renderPredictionsTable = () => (
    <table className="om-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('material_number')} style={{ cursor: 'pointer' }}>Nr mat. {sortIndicator('material_number')}</th>
          <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>Nazwa materiału{sortIndicator('description')}</th>
          <th onClick={() => handleSort('average_days')} style={{ cursor: 'pointer' }}>Średnia (dni robocze){sortIndicator('average_days')}</th>
          <th onClick={() => handleSort('median_days')} style={{ cursor: 'pointer' }}>Mediana (dni robocze){sortIndicator('median_days')}</th>
          <th onClick={() => handleSort('samples')} style={{ cursor: 'pointer' }}>Próbki{sortIndicator('samples')}</th>
        </tr>
      </thead>
      <tbody>
        {pagination.pageItems.map((item, i) => (
          <tr key={i}>
            <td><strong>{item.material_number}</strong></td>
            <td>{item.description}</td>
            <td>{item.average_days}</td>
            <td>{item.median_days}</td>
            <td>{item.samples}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderOldNewTable = () => (
    <table className="om-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('material_number')} style={{ cursor: 'pointer' }}>Nowy Nr mat. {sortIndicator('material_number')}</th>
          <th onClick={() => handleSort('material_number_old')} style={{ cursor: 'pointer' }}>Stary Nr mat. {sortIndicator('material_number_old')}</th>
          <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>Nazwa materiału{sortIndicator('description')}</th>
        </tr>
      </thead>
      <tbody>
        {pagination.pageItems.map((item, i) => (
          <tr key={i}>
            <td><strong>{item.material_number}</strong></td>
            <td>{item.material_number_old}</td>
            <td>{item.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderTable = () => {
    if (activeTab === 'pending') return renderPendingTable();
    if (activeTab === 'predictions') return renderPredictionsTable();
    if (activeTab === 'oldnew') return renderOldNewTable();
    return null;
  };

  return (
    <div className="page-container order-monitoring">
      {syncing && (
        <div className="om-overlay">
          <div className="om-overlay__box">
            <div className="om-overlay__spinner" />
            <div className="om-overlay__text">
              Synchronizacja, proszę czekać
            </div>
          </div>
        </div>
      )}
      <div className="page-header">
        <h2 className="module-page-title"><PackageSearch aria-hidden="true" /> Monitorowanie zamówień Winkhaus</h2>
        <p>Śledzenie zamówień Winkhaus, przeglądanie oczekujących materiałów, przewidywanie terminów dostaw oraz informacje o zmienionych numerach materiałów.</p>
      </div>

      <div className="page-content">
        {/* Toolbar */}
        <div className="om-toolbar">
          <div className="om-tabs">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`om-tab ${activeTab === tab.key ? 'om-tab--active' : ''}`}
                onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', tab.key); return next; })}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="om-controls">
            <div className="om-dept-toggle">
              {DEPARTMENTS.map(dept => (
                <button
                  key={dept}
                  className={`om-dept-btn ${department === dept ? 'om-dept-btn--active' : ''}`}
                  onClick={() => setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('department', dept);
                    if (dept === 'ALU') next.delete('deliveryAddress');
                    return next;
                  })}
                >
                  {dept}
                  {departmentHasDuplicateWarning(dept) && (
                    <span
                      className="om-duplicate-warning-icon"
                      title={`Wykryto możliwe podwójne zamówienie: ${dept}`}
                      aria-label={`Wykryto możliwe podwójne zamówienie: ${dept}`}
                    >
                      ⚠
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* <button
              className="om-sync-btn"
              onClick={handleSync}
              disabled={syncing}
              title="Synchronizacja e-maili od dostawcy Winkhaus."
            >
              {syncing ? '⏳ Syncing…' : '🔄 Synchron.'}
            </button> */}

            {activeTab === 'pending' && (
              <button
                className={`om-sync-btn ${sortByLatestChanges ? 'om-btn-active' : ''}`}
                onClick={handleSortByLatestChanges}
                title="Sortowanie według ostatnich zmian terminów dostaw."
              >
                {sortByLatestChanges ? '✓ Ostatnie zmiany' : 'Ostatnie zmiany'}
              </button>
            )}

            <div
              className="om-export-wrapper"
              style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
            >
              <button
                className="om-sync-btn"
                disabled={exporting || loading || processedData.length === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setExportMenuOpen(!exportMenuOpen);
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                <Download size={16} aria-hidden="true" />
                Export
                {exportMenuOpen
                  ? <ChevronUp size={15} aria-hidden="true" />
                  : <ChevronDown size={15} aria-hidden="true" />}
              </button>
              {exportMenuOpen && (
                <div
                  className="om-export-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 9999,
                    background: '#fff',
                    border: '1px solid #ccc',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '160px',
                    width: '160px',
                    padding: '8px 0',
                    display: 'block',
                  }}
                >
                  <button style={{ width: '100%' }} onClick={() => handleExport('csv')}>CSV</button>
                  <button style={{ width: '100%' }} onClick={() => handleExport('xlsx')}>Excel (.xlsx)</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sync result banner */}
        {syncResult && (
          <div className="om-banner om-banner--success">
            Synced {syncResult.new_emails_processed} email(s) —{' '}
            {syncResult.delivery_items_saved} delivery items,{' '}
            {syncResult.order_items_saved} order items saved.
            {syncResult.synced_at && (
              <span style={{ marginLeft: '1rem', opacity: 0.8 }}>
                (Last sync: {new Date(syncResult.synced_at).toLocaleString()})
              </span>
            )}
          </div>
        )}

        {/* Last sync info */}
        {lastSync && !syncResult && (
          <div className="om-banner om-banner--info" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
            Ostatnia synchronizacja: {new Date(lastSync.synced_at).toLocaleString()}
            {' '}({lastSync.new_emails_processed} email(s), {lastSync.items_saved} item(s))
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="om-banner om-banner--error">⚠️ {error}</div>
        )}

        {duplicateOrderAlerts.map(alert => (
          <div
            key={`${alert.duplicate_scope}:${alert.order_number}:${alert.similar_order_number}`}
            className="om-banner om-banner--warning"
            role="alert"
          >
            <strong>⚠️ Możliwe podwójne zamówienie:</strong>{' '}
            <strong>{alert.duplicate_scope}</strong>{' — '}
            zamówienie <strong>{alert.order_number}</strong> z {alert.order_date} jest w{' '}
            <strong>{alert.similarity_percent}%</strong> podobne do zamówienia{' '}
            <strong>{alert.similar_order_number}</strong> z {alert.similar_order_date}
            {' '}({alert.shared_materials} z {alert.compared_materials} wspólnych materiałów).
            {' '}Zweryfikuj zamówienia przed dalszą realizacją.
          </div>
        ))}

        {/* Order delivery status lookup */}
        <form className="om-order-lookup" onSubmit={handleOrderStatusLookup}>
          <div className="om-order-autocomplete">
            <input
              type="text"
              role="combobox"
              className="om-search"
              placeholder="Wpisz Nr zam. lub Zam. klienta"
              aria-label="Numer zamówienia lub zamówienie klienta"
              aria-autocomplete="list"
              aria-controls="order-identifier-suggestions"
              aria-expanded={orderSuggestionsOpen}
              aria-activedescendant={
                activeOrderSuggestion >= 0
                  ? `order-identifier-suggestion-${activeOrderSuggestion}`
                  : undefined
              }
              autoComplete="off"
              value={orderIdentifier}
              onChange={e => {
                orderStatusFetchIdRef.current += 1;
                setOrderIdentifier(e.target.value);
                setOrderStatus(null);
                setOrderStatusError(null);
                setOrderStatusLoading(false);
              }}
              onFocus={() => {
                if (orderSuggestions.length > 0) setOrderSuggestionsOpen(true);
              }}
              onBlur={() => setOrderSuggestionsOpen(false)}
              onKeyDown={handleOrderIdentifierKeyDown}
            />
            {orderSuggestionsOpen && (
              <ul
                id="order-identifier-suggestions"
                className="om-order-suggestions"
                role="listbox"
              >
                {orderSuggestions.map((suggestion, index) => (
                  <li
                    id={`order-identifier-suggestion-${index}`}
                    key={`${suggestion.kind}:${suggestion.value}`}
                    role="option"
                    aria-selected={index === activeOrderSuggestion}
                    className={index === activeOrderSuggestion ? 'is-active' : ''}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => selectOrderSuggestion(suggestion)}
                  >
                    <strong>{suggestion.value}</strong>
                    <span>
                      {suggestion.kind === 'order_number'
                        ? 'Numer zamówienia'
                        : `Zamówienie klienta · Nr zam. ${suggestion.order_number}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {orderSuggestionsLoading && (
              <span className="om-order-suggestions-loading" aria-live="polite">
                Szukanie…
              </span>
            )}
          </div>
          <button
            type="submit"
            className="om-order-lookup-btn"
            disabled={orderStatusLoading || !orderIdentifier.trim()}
            title="Sprawdź status dostawy zamówienia"
          >
            <Search size={18} aria-hidden="true" />
            {orderStatusLoading ? 'Sprawdzanie...' : 'Sprawdź zamówienie'}
          </button>
        </form>

        <div aria-live="polite">
          {orderStatus?.map(item => (
            <div
              key={item.order_number}
              className={`om-banner ${item.all_delivered ? 'om-banner--success' : 'om-banner--info'}`}
            >
              <strong>Nr zam. {item.order_number}</strong>
              {item.customer_order_designation && ` (Zam. klienta: ${item.customer_order_designation})`}
              {item.delivery_address && (
                <> — Lokalizacja: <strong>{item.delivery_address}</strong></>
              )}
              {' — '}
              {item.all_delivered
                ? 'całe zamówienie zostało dostarczone.'
                : item.last_item_date
                  ? `ostatni element jest planowany na ${item.last_item_date}.`
                  : 'zamówienie nie zostało jeszcze dostarczone w całości; brak potwierdzonego terminu ostatniego elementu.'}
            </div>
          ))}
          {orderStatusError && (
            <div className="om-banner om-banner--error">{orderStatusError}</div>
          )}
        </div>

        {department === 'PVC' && (
          <div
            className="om-delivery-filter"
            role="group"
            aria-label="Filtr adresu dostawy"
          >
            {[
              { value: '', label: 'Wszystkie' },
              ...DELIVERY_ADDRESSES.map(address => ({ value: address, label: address })),
            ].map(option => (
              <button
                key={option.value || 'all'}
                type="button"
                className={`om-delivery-filter-btn ${deliveryAddress === option.value ? 'om-delivery-filter-btn--active' : ''}`}
                aria-pressed={deliveryAddress === option.value}
                onClick={() => setSearchParams(prev => {
                  const next = new URLSearchParams(prev);
                  if (option.value) {
                    next.set('deliveryAddress', option.value);
                  } else {
                    next.delete('deliveryAddress');
                  }
                  return next;
                })}
              >
                {option.label}
                {deliveryAddressHasDuplicateWarning(option.value) && (
                  <span
                    className="om-duplicate-warning-icon"
                    title={`Wykryto możliwe podwójne zamówienie: ${option.label}`}
                    aria-label={`Wykryto możliwe podwójne zamówienie: ${option.label}`}
                  >
                    ⚠
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Table search bar */}
        <div className="om-search-row">
          <input
            type="text"
            className="om-search"
            placeholder="Wyszukaj po wszystkich kolumnach"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          {/* Material lookup — only on predictions tab */}
          {activeTab === 'predictions' && (
            <form className="om-lookup-form" onSubmit={handleMaterialLookup}>
              <input
                type="text"
                className="om-lookup-input"
                placeholder="Numer materiału"
                value={lookupMaterial}
                onChange={e => setLookupMaterial(e.target.value)}
              />
              <button type="submit" className="om-lookup-btn">🔍</button>
            </form>
          )}
        </div>

        {/* Lookup result */}
        {lookupResult?.length > 0 && (
          <div className="om-banner om-banner--info">
            {lookupResult.map((item) => (
              <div key={`${item.material_number}:${item.description || ''}`}>
                <strong>{item.material_number}</strong>
                {item.description ? ` — ${item.description}` : ''}
                {' — '}Avg: {item.average_days} days, Median: {item.median_days} days ({item.samples} samples)
              </div>
            ))}
          </div>
        )}
        {lookupError && (
          <div className="om-banner om-banner--error">{lookupError}</div>
        )}

        {/* Data table */}
        <div className="om-table-wrap">
          {loading ? (
            <div className="om-loading">Ładowanie danych…</div>
          ) : processedData.length === 0 ? (
            <div className="om-empty">Brak danych dostępnych dla {department}.</div>
          ) : (
            <>
              {renderTable()}
              <TablePagination pagination={pagination} unfilteredTotal={data.length} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductionOrderMonitoring;
